import { useMemo } from 'react';
import { useSelector } from 'react-redux';
// Компоненты пользовательского интерфейса
import '../../styles/TableMap.css';
import CustomButtonEdit from '../CustomElements/CustomButtonEdit';
import CustomButton from '../CustomElements/CustomButton';
import Selector from '../CustomElements/Selector';
// Кастомные хуки
import { useRole } from '../../customHook/useRole';
// Утилиты
import { convertDateToString } from '../../utils/convertToDate';
import { ACTION_TYPE, STATUS_NAME } from '../../constants/status';

// actionId  - ID действий:
// 1 - Выдача
// 2 - Хранение
// 3 - Направление в ремонт
// 4 - Списание
// 5 - Выполнение ТО
// 6 - Изменение статуса → Эксплуатация
// 7 - Изменение статуса → Хранение
// 8 - Изменение статуса → Ремонт

const pathUploads = process.env.REACT_APP_UPLOADS;

const StatusHeader = ({ title, date, language, isEditable, onEditClick, getTranslatedStatus }) => {
  return (
    <div className="date-button-container">
      <div className="date">
        {getTranslatedStatus(title, language)} {date}
      </div>
      {isEditable && <CustomButtonEdit onClick={onEditClick} />}
    </div>
  );
};

const GridColumn = ({ children }) => <div className="grid-column">{children}</div>;

const ColumnItem = ({ title, children, valueClassName = '' }) => {
  const titleWithColon = typeof title === 'string' && !title.trim().endsWith(':') ? `${title}:` : title;

  return (
    <div className="column-item">
      <div className="column-title">{titleWithColon}</div>
      <div className={`column-value ${valueClassName}`}>{children}</div>
    </div>
  );
};
const ArchiveFilters = ({
  t,
  optionsForSelect,
  selectedDateSort,
  setSelectedDateSort,
  selectedStatus,
  setSelectedStatus,
}) => {
  return (
    <div className="device-archive-status_select-container">
      <Selector
        column="left"
        options={optionsForSelect.optionsDateSort}
        onChange={setSelectedDateSort}
        placeholder={t?.placeholderDateSortNew || 'Сначала новые'}
        width="var(--sadr-font-size-320)"
        height="var(--sadr-font-size-60)"
        className="device-archive__buttons_fit"
        value={selectedDateSort}
        isMulti={false}
        name="dateSort"
        isView
        defaultValue={selectedDateSort}
      />
      <Selector
        column="left"
        options={optionsForSelect.optionsActions}
        onChange={setSelectedStatus}
        placeholder={t?.placeholderAllActions || 'Все действия'}
        width="var(--sadr-font-size-320)"
        height="var(--sadr-font-size-60)"
        className="device-archive__buttons_fit"
        value={selectedStatus}
        isMulti={false}
        name="typeStatus"
        isView
      />
    </div>
  );
};
const StatusFilterButtons = ({ buttons = [], activeButton, onSelect }) => {
  if (!buttons.length) return null;

  return (
    <div className="table-map-button-container">
      {buttons.map((buttonName) => (
        <CustomButton
          key={buttonName}
          text={buttonName}
          className={`device_button ${activeButton === buttonName ? 'activeBtn' : ''}`}
          colorText={activeButton === buttonName ? 'var(--sadr-success)' : 'var(--sadr-label-color)'}
          onClick={() => onSelect(buttonName)}
        />
      ))}
    </div>
  );
};

const FileLink = ({ fileId, fileName }) => {
  if (!fileId || !fileName) return null;

  return (
    <a
      className="link link-truncate"
      href={`${pathUploads}?fileId=${encodeURIComponent(fileId)}&fileName=${encodeURIComponent(fileName)}`}
    >
      {fileName}
    </a>
  );
};

const TableMap = ({
  data = [],
  handleButtonClick,
  isBtn = true,
  setActiveButton,
  activeButton,
  archive = false,
  setSelectedStatus,
  setSelectedDateSort,
  selectedStatus,
  selectedDateSort,
  t,
}) => {
  //проверяем роли пользователя
  const isMaster = useRole('master');

  // Получаем опции
  const optionsForSelect = useSelector((state) => state.options);

  const btnName = useMemo(
    () => [
      t?.statusOperation || 'Эксплуатация',
      t?.statusStorage || 'Хранение',
      t?.statusRepair || 'Ремонт',
      t?.statusArchive || 'Архив',
    ],
    [t]
  );

  // записываем в стейт имя кнопки для отрисовки компонента
  const selectComponentButton = (buttonName) => {
    if (buttonName !== activeButton) {
      setActiveButton(buttonName);
    }
  };

  const statusDictionary = {
    // Russian: [Serbian, English]
    Хранение: ['Skladištenje', 'Storage'],
    'Изменение данных статуса Хранение': ['Ažuriranje skladištenja', 'Storage status update'],
    'Выполнение ТО': ['Održavanje', 'Maintenance'],
    Выдача: ['Izdavanje', 'Issuance'],
    'Изменение данных статуса Эксплуатация': ['Ažuriranje eksploatacije', 'Operation status update'],
    Возврат: ['Povratak', 'Return'],
    'Направление в ремонт': ['Poslato na popravku', 'Sent for repair'],
    'Изменение данных статуса Ремонт': ['Ažuriranje popravke', 'Repair status update'],
    'Возврат из ремонта': ['Vraćeno sa popravke', 'Returned from repair'],
    Списание: ['Otpisan', 'Write-off'],
  };

  const currentLanguage = useSelector((state) => state.language);

  const getTranslatedStatus = (status, language) => {
    if (!statusDictionary[status]) return status; // Return original if not in dictionary

    switch (language) {
      case 'SR':
        return statusDictionary[status][0] || status; // Serbian
      case 'EN':
        return statusDictionary[status][1] || status; // English
      default:
        return status; // Russian (default)
    }
  };

  const getConditionText = (broken, t) => {
    if (broken === null || broken === undefined) {
      return t?.deviceConditionUndefined || 'Не определено';
    }
    return broken ? t?.deviceConditionBroken || 'Неисправно' : t?.deviceConditionWorking || 'Исправно';
  };

  const getBtnName = (title, t) => {
    if (title === t?.statusStorage || title === 'Хранение') {
      return 'edit';
    }
    return title || 'edit';
  };

  /**
   * Вычисляет логические флаги статуса действия устройства.
   * @param {Object} status - Объект статуса устройства.
   * @returns {Object} Объект с булевыми флагами по типу действия.
   */
  function getStatusFlags(status) {
    const { actionId, actionTitle } = status;
    // Списание (например, утилизация)
    const isWriteOff = actionId === ACTION_TYPE.WRITE_OFF;
    // Техническое обслуживание
    const isService = actionId === ACTION_TYPE.SERVICE;
    // Проверка: это действие связано с хранением (или его изменением)
    const isStorageAction = [ACTION_TYPE.STORAGE, ACTION_TYPE.STORAGE_CHANGE].includes(actionId);
    // Проверка: это действие связано с эксплуатацией (выдачей или изменением)
    const isUsageAction = [ACTION_TYPE.ISSUANCE, ACTION_TYPE.USAGE_CHANGE].includes(actionId);
    // Проверка: это действие связано с ремонтом (направлением или изменением)
    const isRepairAction = [ACTION_TYPE.REPAIR, ACTION_TYPE.REPAIR_CHANGE].includes(actionId);

    return {
      // Статус: хранение или изменение статуса на "хранение"
      isStorageStatus: isStorageAction && (!actionTitle || actionTitle === STATUS_NAME.STORAGE),
      // Статус: возврат устройства на хранение
      isReturn: isStorageAction && actionTitle === STATUS_NAME.USAGE,
      // Статус: эксплуатация (выдача или изменение данных статуса)
      isUsageStatus: isUsageAction && (!actionTitle || actionTitle === STATUS_NAME.USAGE),
      // Статус: ремонт (направление или редактирование)
      isRepairStatus: isRepairAction && (!actionTitle || actionTitle === STATUS_NAME.REPAIR),
      // Статус: возврат из ремонта (устройство возвращено в хранилище после ремонта)
      isReturnFromRepair: isStorageAction && actionTitle === STATUS_NAME.REPAIR,
      // Списание
      isWriteOff,
      // Проведение ТО
      isService,
    };
  }

  return (
    <div className="table-map-container">
      <div className="shadow"></div>
      {/* Блок с кнопками для выбора статусов */}
      <StatusFilterButtons buttons={btnName} activeButton={activeButton} onSelect={selectComponentButton} />
      {archive && (
        <ArchiveFilters
          t={t}
          optionsForSelect={optionsForSelect}
          selectedDateSort={selectedDateSort}
          setSelectedDateSort={setSelectedDateSort}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
        />
      )}
      {/* Если массив пустой выводим запись что данных нет */}
      {data?.length === 0 ? (
        <div className="table-map-no-data">{t?.noDataAvailable || 'Данные отсутствуют'}</div>
      ) : (
        data?.map((status, index) => {
          // Составление уникального ключа, объединяя несколько значений
          const uniqueKey = `${status?.id}_${status?.deviceId}_${index}`;
          //определяем имя для кнопки чтобы передать в функцию для открытия модального окна по имени
          const btnName = getBtnName(status?.title, t);
          //форматируем дату
          const formattedDate = convertDateToString(status?.date);
          //определяем состояние
          const conditionText = getConditionText(status?.broken, t);
          const isUipps = status?.uipps;
          const isPlacement = status?.placement;

          const {
            isWriteOff,
            isService,
            isStorageStatus,
            isReturn,
            isUsageStatus,
            isRepairStatus,
            isReturnFromRepair,
          } = getStatusFlags(status);

          return (
            // Таблица статусов
            <div key={uniqueKey} className="table-map-row">
              {/* Дата и кнопка */}
              <StatusHeader
                title={status?.title}
                date={formattedDate}
                language={currentLanguage}
                isEditable={isBtn && !isMaster}
                getTranslatedStatus={getTranslatedStatus}
                onEditClick={() => handleButtonClick(btnName, status?.id)}
              />

              {/* Трех колоночная сетка */}
              <div className="grid-columns">
                {/* Колонка 1 */}
                {/* Хранение */}
                {/* (status?.title === 'Хранение' || status?.title === 'Изменение данных статуса Хранение') */}
                {isStorageStatus && (
                  <GridColumn>
                    <ColumnItem title={t?.acceptanceCertificateTitle || 'Акт приема-передачи'}>
                      <FileLink
                        fileId={status?.acceptanceCert}
                        fileName={status?.acceptanceCertName}
                        pathUploads={pathUploads}
                      />
                    </ColumnItem>
                    <ColumnItem title={t?.storageLocationTitle || 'Место хранения'}>{status?.storage}</ColumnItem>
                  </GridColumn>
                )}
                {/* ТО*/}
                {isService && (
                  <GridColumn>
                    <ColumnItem title={t?.serviceDateTitle || 'Дата ТО'}>
                      {convertDateToString(status?.serviceDate)}
                    </ColumnItem>
                    <ColumnItem title={t?.nextServiceDateTitle || 'Дата следующего ТО'}>
                      {convertDateToString(status?.nextDate)}
                    </ColumnItem>
                  </GridColumn>
                )}
                {/* Эксплуатация выдача */}
                {/* status?.title === 'Выдача'|| status?.title === 'Изменение данных статуса Эксплуатация' */}
                {isUsageStatus && (
                  <GridColumn>
                    {!isUipps && (
                      <ColumnItem title={t?.senderFullNameTitle || 'ФИО выдавшего'}>{status?.sender}</ColumnItem>
                    )}
                    <ColumnItem title={t?.acceptanceCertificateTitle || 'Акт приема-передачи'}>
                      <FileLink
                        fileId={status?.acceptanceCert}
                        fileName={status?.acceptanceCertName}
                        pathUploads={pathUploads}
                      />
                    </ColumnItem>
                    <ColumnItem title={t?.employeeIdTitle || 'Таб. № принявшего'}>{status?.responsibleId}</ColumnItem>
                  </GridColumn>
                )}
                {/* Эксплуатация возврат */}
                {/* status?.title === 'Возврат' */}
                {isReturn && (
                  <GridColumn>
                    <ColumnItem title={t?.receivedByTitle || 'ФИО принявшего'}>{status?.receiver}</ColumnItem>
                    <ColumnItem title={t?.acceptanceCertificateTitle || 'Акт приема-передачи'}>
                      <FileLink
                        fileId={status?.acceptanceCert}
                        fileName={status?.acceptanceCertName}
                        pathUploads={pathUploads}
                      />
                    </ColumnItem>
                    <ColumnItem title={t?.storageLocationTitle || 'Место хранения'}>{status?.storage}</ColumnItem>
                  </GridColumn>
                )}
                {/* Ремонт Направление в ремонт */}
                {/* status?.title === 'Направление в ремонт' || status?.title === 'Изменение данных статуса Ремонт' */}
                {isRepairStatus && (
                  <GridColumn>
                    <ColumnItem title={t?.acceptanceCertificateTitle || 'Акт приема-передачи'}>
                      <FileLink
                        fileId={status?.acceptanceCert}
                        fileName={status?.acceptanceCertName}
                        pathUploads={pathUploads}
                      />
                    </ColumnItem>
                    <ColumnItem title={t?.serviceCenterTitle || 'Сервисный центр'}>{status?.serviceCenter}</ColumnItem>
                    <ColumnItem title={t?.serviceCenterContactTitle || 'Контакты центра'}>
                      {status?.serviceCenterContact}
                    </ColumnItem>
                  </GridColumn>
                )}
                {/* Ремонт Возврат из ремонта */}
                {/* status?.title === 'Возврат из ремонта' */}
                {isReturnFromRepair && (
                  <GridColumn>
                    <ColumnItem title={t?.acceptanceCertificateTitle || 'Акт приема-передачи'}>
                      <FileLink
                        fileId={status?.acceptanceCert}
                        fileName={status?.acceptanceCertName}
                        pathUploads={pathUploads}
                      />
                    </ColumnItem>
                    <ColumnItem title={t?.receivedByTitle || 'ФИО принявшего'}>{status?.receiver}</ColumnItem>
                  </GridColumn>
                )}
                {/* Архив*/}
                {isWriteOff && (
                  <GridColumn>
                    <ColumnItem title={t?.writeOffDateTitle || 'Дата списания'}>
                      {convertDateToString(status?.date)}
                    </ColumnItem>
                  </GridColumn>
                )}
                {/* Колонка 2 */}
                {/* Хранение */}
                {/* (status?.title === 'Хранение' || status?.title === 'Изменение данных статуса Хранение') */}
                {isStorageStatus && (
                  <GridColumn>
                    <ColumnItem title={t?.receivedByTitle || 'ФИО принявшего'}>{status?.receiver}</ColumnItem>
                    <ColumnItem title={t?.deviceConditionTitle || 'Состояние'}>{conditionText}</ColumnItem>
                  </GridColumn>
                )}
                {/* ТО*/}
                {isService && (
                  <GridColumn>
                    <ColumnItem title={t?.worksDoneTitle || 'Выполненные работы'}>{status?.worksDone}</ColumnItem>
                  </GridColumn>
                )}
                {/* Эксплуатация выдача */}
                {/* status?.title === 'Выдача' || status?.title === 'Изменение данных статуса Эксплуатация'*/}
                {isUsageStatus && !isUipps && (
                  <GridColumn>
                    <ColumnItem title={t?.roadTitle || 'Дорога'}>{status?.road}</ColumnItem>
                    <ColumnItem title={t?.directorateTitle || 'Дирекция'}>{status?.directorate}</ColumnItem>
                    <ColumnItem title={t?.departmentTitle || 'Подразделение'}>{status?.department}</ColumnItem>
                  </GridColumn>
                )}
                {/* Эксплуатация выдача для уиппс*/}
                {/* status?.title === 'Выдача' || status?.title === 'Изменение данных статуса Эксплуатация'*/}
                {isUsageStatus && isUipps && (
                  <GridColumn>
                    <ColumnItem title={t?.roadTitle || 'Дорога'}>{status?.road}</ColumnItem>
                    <ColumnItem title={t?.directorateTitle || 'Дирекция'}>{status?.directorate}</ColumnItem>
                    <ColumnItem title={t?.departmentTitle || 'Подразделение'}>{status?.department}</ColumnItem>
                  </GridColumn>
                )}
                {/* Эксплуатация возврат */}
                {/* status?.title === 'Возврат' */}
                {isReturn && (
                  <GridColumn>
                    <ColumnItem title={t?.deviceConditionTitle || 'Состояние'}>{conditionText}</ColumnItem>
                    <ColumnItem title={t?.defectsReportTitle || 'Дефектная ведомость'}>
                      <FileLink
                        fileId={status?.attachment}
                        fileName={status?.attachmentName}
                        pathUploads={pathUploads}
                      />
                    </ColumnItem>
                  </GridColumn>
                )}
                {/* Ремонт Направление в ремонт */}
                {/* (status?.title === 'Направление в ремонт' || status?.title === 'Изменение данных статуса Ремонт')  */}
                {isRepairStatus && (
                  <GridColumn>
                    <ColumnItem title={t?.senderResponsibleFullNameTitle || 'ФИО направившего'}>
                      {status?.sender}
                    </ColumnItem>
                    <ColumnItem title={t?.defectsReportTitle || 'Дефектная ведомость'}>
                      <FileLink
                        fileId={status?.defectsFile}
                        fileName={status?.defectsFileName}
                        pathUploads={pathUploads}
                      />
                    </ColumnItem>
                    <ColumnItem title={t?.deviceConditionTitle || 'Состояние'}>{conditionText}</ColumnItem>
                  </GridColumn>
                )}
                {/* Ремонт Возврат из ремонта */}
                {status?.title === 'Возврат из ремонта' && (
                  <GridColumn>
                    <ColumnItem title={t?.deviceConditionTitle || 'Состояние'}>{conditionText}</ColumnItem>
                    <ColumnItem title={t?.serviceReportTitle || 'Акт выполненных работ'}>
                      <FileLink
                        fileId={status?.attachment}
                        fileName={status?.attachmentName}
                        pathUploads={pathUploads}
                      />
                    </ColumnItem>
                  </GridColumn>
                )}
                {/* Архив*/}
                {isWriteOff && (
                  <GridColumn>
                    <ColumnItem title={t?.writeOffActTitle || 'Акт списания'}>
                      <FileLink
                        fileId={status?.attachment}
                        fileName={status?.attachmentName}
                        pathUploads={pathUploads}
                      />
                    </ColumnItem>
                  </GridColumn>
                )}
                {/* Колонка 3 */}
                {/* Хранение */}
                {/* (status?.title === 'Хранение' || status?.title === 'Изменение данных статуса Хранение') */}
                {isStorageStatus && (
                  <GridColumn>
                    <ColumnItem title={t?.commentTitle || 'Комментарий'} valueClassName="column-value-note">
                      {status?.note}
                    </ColumnItem>
                  </GridColumn>
                )}
                {/* ТО*/}
                {isService && (
                  <GridColumn>
                    <ColumnItem title={t?.commentTitle || 'Комментарий'} valueClassName="column-value-note">
                      {status?.note}
                    </ColumnItem>
                  </GridColumn>
                )}
                {/* Эксплуатация выдача */}
                {/* status?.title === 'Выдача' || status?.title === 'Изменение данных статуса Эксплуатация'*/}
                {isUsageStatus && !isUipps && (
                  <GridColumn>
                    <div className="column-item">
                      <div className="column-title">{t?.responsibleContactTitle || 'Контакты'}:</div>
                      <div className="column-value column-value-note">{status?.responsibleContact}</div>
                    </div>
                  </GridColumn>
                )}
                {/* Эксплуатация выдача для уиппс станционное размещение */}
                {/* status?.title === 'Выдача'|| status?.title === 'Изменение данных статуса Эксплуатация' */}
                {isUsageStatus && isUipps && isPlacement && (
                  <GridColumn>
                    <ColumnItem title={t?.placementTypeTitle || 'Тип ПС'}>{status?.trainType}</ColumnItem>
                    <ColumnItem title={t?.placementNumberTitle || 'Номер ПС'}>{status?.trainNumber}</ColumnItem>
                  </GridColumn>
                )}
                {/* Эксплуатация выдача для уиппс не станционное размещение*/}
                {/* status?.title === 'Выдача'|| status?.title === 'Изменение данных статуса Эксплуатация' */}
                {isUsageStatus && status?.uipps && !isPlacement && (
                  <GridColumn>
                    <ColumnItem title={t?.driverIdTitle || 'Табельный № машиниста'} valueClassName="column-value-note">
                      {status?.driverId}
                    </ColumnItem>
                    <ColumnItem
                      title={t?.driverContactTitle || 'Контакты машиниста'}
                      valueClassName="column-value-note"
                    >
                      {status?.responsibleContact}
                    </ColumnItem>
                  </GridColumn>
                )}
                {/* Эксплуатация возврат */}
                {/* status?.title === 'Возврат' */}
                {isReturn && (
                  <GridColumn>
                    <ColumnItem title={t?.commentTitle || 'Комментарий'} valueClassName="column-value-note">
                      {status?.note}
                    </ColumnItem>
                  </GridColumn>
                )}
                {/* Ремонт Направление в ремонт */}
                {/* (status?.title === 'Направление в ремонт' || status?.title === 'Изменение данных статуса Ремонт') */}
                {isRepairStatus && (
                  <GridColumn>
                    <ColumnItem title={t?.commentTitle || 'Комментарий'} valueClassName="column-value-note">
                      {status?.note}
                    </ColumnItem>
                  </GridColumn>
                )}
                {/* Ремонт Возврат из ремонта */}
                {/* status?.title === 'Возврат из ремонта' */}
                {isReturnFromRepair && (
                  <GridColumn>
                    <ColumnItem title={t?.commentTitle || 'Комментарий'} valueClassName="column-value-note">
                      {status?.note}
                    </ColumnItem>
                  </GridColumn>
                )}
                {/* Архив*/}
                {isWriteOff && (
                  <GridColumn>
                    <ColumnItem title={t?.writeOffReasonTitle || 'Причина списания'} valueClassName="column-value-note">
                      {status?.note}
                    </ColumnItem>
                  </GridColumn>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};
export default TableMap;
